import { useEffect, useState } from 'react'
import { sidebarStore } from '../components/Sidebar'

/**
 * Hook to check if the current user has module permission for a given module.
 *
 * Uses the sidebar store data to determine permission - if the module's menu item
 * exists, the user is authorized. Also returns role information.
 *
 * @param moduleId - The module identifier to check (e.g., 'pec', 'enps')
 * @returns Object with isLoading, isAuthorized, and role states
 */
export function useModulePermission(moduleId: string): {
	isLoading: boolean
	isAuthorized: boolean
	role: 'admin' | 'viewerAdmin' | 'both' | undefined
	isViewerAdmin: boolean
	isModuleAdmin: boolean
} {
	const { state, initialize } = sidebarStore()
	const [isLoading, setIsLoading] = useState(true)

	useEffect(() => {
		if (state.menuItems.length > 0) {
			setIsLoading(false)
			return
		}

		const loadPermissions = async () => {
			try {
				await initialize()
			} finally {
				setIsLoading(false)
			}
		}

		loadPermissions()
	}, [state.menuItems.length, initialize])

	const menuItem = state.menuItems.find((item) => item.id === moduleId)
	const rawRole = menuItem?.role
	const validRoles = ['admin', 'viewerAdmin', 'both'] as const
	const role = validRoles.includes(rawRole as (typeof validRoles)[number])
		? (rawRole as 'admin' | 'viewerAdmin' | 'both')
		: undefined

	if (menuItem && rawRole && !role) {
		console.warn(`[useModulePermission] Unknown role "${rawRole}" for module "${moduleId}"`)
	}

	return {
		isLoading,
		isAuthorized: !!menuItem,
		role,
		isViewerAdmin: role === 'viewerAdmin' || role === 'both',
		isModuleAdmin: role === 'admin' || role === 'both',
	}
}
